import { get, isEqual, mergeWith, set, isPlainObject, cloneDeep } from 'lodash';
import { action, autorun, observable, reaction, toJS } from 'mobx';
import type { UserStore } from '~/stores/user.store';
import type { UserService } from '~/services/user.service';
import type { InjectorService } from '~/services/injector.service';
import { trackError } from '~/services/logger/error.utils';
import { UiPersistence } from '~/types/angular.types';
import { Paths, DeepValue } from '~/types/app.types';
import { isReactFirst } from '~/utils/environment.utils';
export class UiPersistenceStore {
    @observable private persistence: UiPersistence = {} as any;

    userService: UserService;
    userStore: UserStore;

    constructor({
        userService,
        userStore,
        injectorService
    }: {
        userService: UserService;
        userStore: UserStore;
        injectorService?: InjectorService;
    }) {
        this.userService = userService;
        this.userStore = userStore;

        // This whole mess will not be needed once we get rid of Angular.
        let disposer: ReturnType<typeof autorun>;
        reaction(
            // Reactions track observables that have been accessed in this first function
            // so we need to access all the observables we want to track here.
            () => [this.userStore.currUser, injectorService.injector],
            () => {
                disposer?.();

                if (
                    injectorService.injector &&
                    process.env.NODE_ENV !== 'test' &&
                    !isReactFirst()
                ) {
                    // We are inside Angular
                    const interval = setInterval(() => {
                        const UiPersistenceService = injectorService.get(
                            // Intentionally not adding UiPersistenceService back to the IAngularServices interface
                            // because i don't want anyone using it directly.
                            'UiPersistenceService' as any
                        );
                        if (UiPersistenceService) {
                            const observableHash = observable(
                                UiPersistenceService.persistenceHash
                            );
                            UiPersistenceService.persistenceHash =
                                observableHash;
                            this.persistence = observableHash;
                            clearInterval(interval);
                        }
                    }, 200);
                } else {
                    disposer = autorun(() => {
                        // If no user, clear persistence
                        if (!this.userStore.currUser) {
                            this.persistence = {} as UiPersistence;

                            return;
                        }

                        this.userStore.currUser?.user_ui_persistences?.forEach(
                            ({ key, value }) => {
                                this.persistence[key] = value;
                            }
                        );
                    });
                }
            }
        );
    }

    get<T extends Paths<UiPersistence>>(
        key: T,
        defaultValue: Partial<DeepValue<UiPersistence, T>>
    ): DeepValue<UiPersistence, T> {
        return get(this.persistence, key) ?? (defaultValue as any);
    }

    @action
    set<T extends Paths<UiPersistence>, U extends DeepValue<UiPersistence, T>>(
        key: T,
        value: Partial<U>
    ) {
        const oldValue = this.get(key, null) as U;

        if (isEqual(oldValue, value)) {
            return;
        }

        const nextValue = isPlainObject(value)
            ? mergeWith({}, toJS(oldValue), toJS(value), (a, b) => {
                  // lodash's default merge will merge arrays element by element, but we want to replace them fully
                  if (Array.isArray(a) && Array.isArray(b)) {
                      return b;
                  }
              })
            : value;

        set(this.persistence, key, nextValue);

        this.postSet(key);
    }

    @action
    replace<
        T extends Paths<UiPersistence>,
        U extends DeepValue<UiPersistence, T>
    >(key: T, value: Partial<U>) {
        const oldValue = this.get(key, null) as U;

        if (isEqual(oldValue, value)) {
            return;
        }
        set(this.persistence, key, cloneDeep(value));

        this.postSet(key);
    }

    postSet<T extends Paths<UiPersistence>>(key: T) {
        try {
            const primaryKey = key.split('.')[0] as keyof UiPersistence;
            const fullValue = this.get(
                primaryKey,
                null
            ) as UiPersistence[keyof UiPersistence];

            this.userService.setUiPersistence(this.userStore.currUser._id, {
                key: primaryKey,
                value: fullValue
            });
        } catch (error) {
            trackError({
                error,
                message: 'UiPersistenceStore: Error setting persistence'
            });
        }
    }
}
